import type { VV } from '@semios/app-platform-value-type-definitions'
import {
  IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX,
  type TEmitterType,
  getValueTypeFromEmitterType,
} from '@semios/app-platform-values-helpers-and-types'
import type { TIcsDashboardData } from '../types'

export const transformValuesToYtdData = ({
  etcResponse,
  iaResponse,
  zoneBlockMappings,
  emitterTypes,
}: {
  etcResponse: Pick<VV.Response, 'blocks'> | null
  iaResponse: Pick<VV.Response, 'irrigationZones'> | null
  zoneBlockMappings: {
    zone_id: number
    block_ids: number[]
  }[]
  emitterTypes: TEmitterType[]
}): TIcsDashboardData['ytd'] => {
  return zoneBlockMappings.map(({ zone_id, block_ids }) => {
    const cumulative_et = block_ids.map((blockId) => {
      const timeseries =
        etcResponse?.blocks?.[String(blockId)]?.values?.evapotranspirationETc?.[0]?.timeseries ?? []

      let cumulativeValue = 0

      const cumulativeTimeseries = timeseries.map((point: { timestamp: string; value: number | null }) => {
        const parsed = Number(point.value)

        cumulativeValue += isNaN(parsed) ? 0 : parsed

        return { timestamp: point.timestamp, value: cumulativeValue }
      })

      return { blockId, evapotranspirationETc: cumulativeTimeseries }
    })

    const dailyTotals: Record<string, { volume: number; duration: number }> = {}

    const iaValueTypeKeys = emitterTypes.map((et) =>
      getValueTypeFromEmitterType(IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX, et),
    )

    const zoneValues = iaResponse?.irrigationZones?.[String(zone_id)]?.values

    if (zoneValues) {
      type TAppliedVolumeKey = keyof typeof zoneValues &
        `${typeof IRRIGATION_APPLIED_VOLUME_VALUE_TYPE_PREFIX}${string}`

      iaValueTypeKeys.forEach((key) => {
        const timeseries = zoneValues[key as TAppliedVolumeKey]?.[0]?.timeseries ?? []

        timeseries.forEach((point) => {
          if (!dailyTotals[point.timestamp]) {
            dailyTotals[point.timestamp] = { volume: 0, duration: 0 }
          }

          dailyTotals[point.timestamp].volume += point.value?.volume_mm ?? 0

          dailyTotals[point.timestamp].duration += point.value?.duration ?? 0
        })
      })
    }

    const sortedDays = Object.entries(dailyTotals).sort((a, b) => a[0].localeCompare(b[0]))

    let cumulativeVolume = 0
    let cumulativeDuration = 0

    const cumulative_ia = sortedDays.map(([timestamp, { volume, duration }]) => {
      cumulativeVolume += volume

      cumulativeDuration += duration

      return { timestamp, value: cumulativeVolume, duration_minutes: cumulativeDuration }
    })

    return { zone_id, cumulative_et, cumulative_ia }
  })
}
